import { PropType, reactive, ref } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import TimeTicketWeekTempWorkerEntity from '@/entity/time-ticket/TimeTicketWeekTempWorkerEntity';
import TimeTicketWeekEntity from '@/entity/time-ticket/TimeTicketWeekEntity';
import { useConfirm, useNotif } from '@orion.ui/orion';
import TimeTicketAttachmentApiTempWorker from '@/api/TimeTicketAttachmentApiTempWorker';
import TimeTicketAttachmentApiClient from '@/api/TimeTicketAttachmentApiClient';
import TimeTicketAttachmentApiAgency from '@/api/TimeTicketAttachmentApiAgency';
import { idsQuery } from '@/utils/tools';
import useMissingDocumentService from '@/services/MissingDocumentService';

type Props = SetupProps<typeof TimeTicketAttachmentAsideSetupService.props>

export default class TimeTicketAttachmentAsideSetupService extends BaseSetupService<Props> {
	static readonly props = {
		timeTicketWeek: {
			type: Object as PropType<TimeTicketWeekTempWorkerEntity | TimeTicketWeekEntity>,
			required: true as const,
		},
	};

	private readonly state = reactive({
		fileTitle: undefined,
		filesUpload: [] as File[],
		selectedFiles: [] as ITimeTicketAttachmentModel[],
	});

	_aside?: OrionAside;
	readonly _upload = ref<OrionUpload>();
	readonly _fileTitle = ref<OrionInput>();

	get fileTitle () { return this.state.fileTitle;}
	set fileTitle (val) { this.state.fileTitle = val;}
	get filesUpload () { return this.state.filesUpload;}
	set filesUpload (val) { this.state.filesUpload = val;}
	get selectedFiles () { return this.state.selectedFiles;}
	set selectedFiles (val) { this.state.selectedFiles = val;}

	get selectedFilesIds () {
		return this.selectedFiles.map(x => x.id);
	}

	get api () {
		if (this.self.isTempWorker()) {
			return TimeTicketAttachmentApiTempWorker;
		} else if (this.self.isClient()) {
			return TimeTicketAttachmentApiClient;
		} else {
			return TimeTicketAttachmentApiAgency;
		}
	}

	constructor (props: Props, aside?: OrionAside) {
		super(props);
		this._aside = aside;
	}

	async uploadNewFileAsync () {
		if (!this.filesUpload.length) return;

		try {
			this._aside?._loader()?.show(`Traîtement de votre document...`);

			const { data } = await TimeTicketAttachmentApiTempWorker.add({
				title: this.fileTitle,
				timeTicketWeekId: this.props.timeTicketWeek.id,
				file: this.filesUpload[0],
			});

			this.props.timeTicketWeek.attachments.push(data);

			this.reset();
			useNotif.success('Votre document a bien été ajouté');
		} finally {
			this._aside?._loader()?.hide();
		}
	}

	reset () {
		this._upload.value?.clear();

		if (this._fileTitle.value) {
			this._fileTitle.value.clear();
			this._fileTitle.value.setHasBeenFocus(false);
		}
	}

	selectFile (file: ITimeTicketAttachmentModel) {
		if (this.fileIsSelected(file)) {
			this.selectedFiles.delete(file);
		} else {
			this.selectedFiles.push(file);
		}
	}

	fileIsSelected (file: ITimeTicketAttachmentModel) {
		return this.selectedFiles.includes(file);
	}

	viewFiles (files: ITimeTicketAttachmentModel[]) {
		const ids = idsQuery(files);
		if (!ids) return;
		this.api.view(ids);
	}

	async downloadFilesAsync (files: ITimeTicketAttachmentModel[]) {
		const ids = idsQuery(files);
		if (!ids) return;
		await this.api.download(ids);
	}

	async deleteFileAsync (file: ITimeTicketAttachmentModel) {
		if (await useConfirm(`Confirmez-vous la suppression du fichier ${file.title} ?`)) {
			this._aside?._loader()?.show(`Suppression du document...`);

			await TimeTicketAttachmentApiTempWorker.delete(file.id);
			this.props.timeTicketWeek.attachments.deleteWhere('id', file.id);
			this.props.timeTicketWeek.attachments.delete(file);
			this._aside?._loader()?.hide();
			useNotif.success('Votre document a bien été supprimé');
		}
	}

	addMissingPreview (e: Event) {
		useMissingDocumentService().useMissingPreview(e);
	}
}
